import React, { useState, useEffect, useCallback } from "react";
import { IoMdClose } from "react-icons/io";
import { Row, Col } from "react-bootstrap";
import debounce from "lodash.debounce";
import {
  Modal,
  Typography,
  TextField,
  Table,
  Button,
  Checkbox,
  TableFooter,
} from "components";
import {
  formatUrlQuery,
  formatFilterPayload,
  setAlert,
  isSuccessResponse,
} from "utils/helper";
import Skeleton from "react-loading-skeleton";
import { routes } from "routes/apiRoutes";
import { useSelector } from "react-redux";
import makeRequest from "services/makeRequest";
import { postMapTerminal } from "../../../../redux/actions/terminalAction";

const MapTerminalModal = ({
  showModal,
  onClose,
  data,
  isLoading,
  terminalDetails,
}) => {
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setSubmitting] = useState(false);
  const [terminalsData, setTerminalsData] = useState({
    terminals: null,
    loading: false,
  });

  const { pickupLocation, id } = terminalDetails;
  /****
   *
   */
  const { user } = useSelector((state) => ({
    user: state.user.user,
  }));

  const initialValues = {
    agentId: user?.agent_id,
    pickupLocation: pickupLocation,
    terminalId: "",
    terminalRequestId: id,
  };

  const [state, setState] = useState(initialValues);

  const initialFilterState = {
    sort: false,
    number: 1,
    status: "UNISSUED",
    oem: "",
    size: 10,
  };

  const [filterState, setFilterState] = useState(initialFilterState);

  const { size, page, oem } = filterState;

  const loadTerminals = async (
    queryParams = formatUrlQuery(formatFilterPayload(filterState))
  ) => {
    setTerminalsData({ ...data, loading: true });
    try {
      const res = await makeRequest("get", routes.fetchTerminals(queryParams));
      setTerminalsData((prev) => ({
        ...prev,
        terminals: res.data.data,
        loading: false,
      }));
    } catch (err) {
      console.log(err);
      setTerminalsData({ ...data, loading: false });
    }
  };

  useEffect(() => {
    loadTerminals();
    //eslint-disable-next-line
  }, [size, page]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadWithDebounce = useCallback(
    debounce((oem) => {
      if (oem === "") {
        loadTerminals();
      } else {
        loadTerminals(`oem=${oem}&status=ENABLED`);
      }
    }, 1000),
    []
  );

  /**
   * This is to pre-populate the "agentId" with value from token in redux state
   * This happen on every page render
   */
  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      agentId: user?.agent_id,
    }));
  }, [user?.agent_id]);

  /**
   *
   * @param {object} event
   */
  // const handleChange = (e) => {
  //   const { name, value } = e.target;
  //   setState((prevState) => ({
  //     ...prevState,
  //     [name]: value,
  //   }));
  // };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterState({ ...filterState, [name]: value });
  };

  /**
   * This is custom validation function
   * @param {object} values
   * @returns
   */
  const validate = (values) => {
    const errors = {};
    if (!values.pickupLocation) {
      errors.pickupLocation = "Pickup Location is required";
    }
    if (!values.terminalId) {
      errors.terminalId = "Please select a terminal";
    }
    return errors;
  };

  /**
   *
   * @param {object} event
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    setFormErrors(validate(state));
    setSubmitting(true);
  };

  /**
   *
   */

  const submitForm = useCallback(() => {
    postMapTerminal([state])
      .then((res) => {
        // Temporary action to make sure we're getting back terminalRequests with updated statuses

        if (isSuccessResponse(res.data)) {
          setAlert(res?.data?.responseMessage, "success");
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          setAlert(res?.data?.responseMessage);
        }
      })
      .catch((err) => {
        setAlert(err);
        setSubmitting(false);
      });
  }, [state]);

  /**
   * This is to track the custom error state on every render
   * This is to submit the form when there is empty error
   */
  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmitting) {
      submitForm();
    }
  }, [formErrors, isSubmitting, submitForm]);

  /**
   *
   */
  const columns = [
    {
      Header: " ",
      Cell: ({ row }) => {
        let TID = row.original.terminalId;
        return (
          <Checkbox
            name={row.id}
            readOnly
            checked={TID === state.terminalId}
            onChange={(e) =>
              state.terminalId !== TID
                ? setState((prev) => ({
                    ...prev,
                    terminalId: TID,
                  }))
                : setState((prev) => ({ ...prev, terminalId: "" }))
            }
          />
        );
      },
    },
    {
      Header: "S/N",
      Cell: (row) => Number(row.row.id) + 1,
    },
    {
      Header: "OEM",
      accessor: "oem",
    },
    {
      Header: "Terminal ID",
      accessor: "terminalId",
    },
    {
      Header: "Serial Number",
      accessor: "serialNumber",
    },
  ];

  return (
    <React.Fragment>
      <Modal showModal={showModal} onClose={onClose} size="lg">
        <Row className="my-4">
          {/* <pre>{JSON.stringify(state,null,2)}</pre> */}
          <Col md={12}>
            <div className="d-flex justify-content-end">
              <IoMdClose className="cursor-pointer" onClick={onClose} />
            </div>
          </Col>
          <Col md={12}>
            <Typography
              variant="subtitle3"
              className="text-center mb-3"
              state="primary"
            >
              Map Terminal
            </Typography>
          </Col>
          <Col md={12} className="mt-3">
            <Row>
              <Col md={12}>
                <TextField
                  name="oem"
                  id="oem"
                  value={oem}
                  onChange={(e) => {
                    setFilterState((prev) => ({
                      ...prev,
                      oem: e.target.value,
                    }));
                    loadWithDebounce(e.target.value);
                  }}
                  placeholder={"Search for terminal  with OEM Name"}
                  size="sm"
                  required={false}
                  searchField
                  className="tiny-bordered-textfield"
                />
              </Col>
              <Col md={12} className="mt-2">
                {isLoading ? (
                  <Skeleton count={15} height={20} />
                ) : (
                  <>
                    <Table
                      columns={columns}
                      data={terminalsData.terminals?.content}
                      centerData
                      loading={terminalsData.loading}
                    />
                    {terminalsData.terminals?.content?.length ? (
                      <TableFooter
                        pageSize={filterState.size}
                        handleChange={handleFilterChange}
                        availableDataLength={
                          terminalsData.terminals.content.length
                        }
                        totalDataLength={terminalsData.terminals.totalElements}
                        handlePageClick={(pageNo) =>
                          setFilterState({ ...filterState, number: pageNo })
                        }
                      />
                    ) : (
                      ""
                    )}
                  </>
                )}
                {formErrors.terminalId && (
                  <small className="text-danger mt-2">
                    {formErrors.terminalId}
                  </small>
                )}
              </Col>
              <Col md={12} className="mt-4">
                <TextField
                  name="pickupLocation"
                  id="pickupLocation"
                  value={state.pickupLocation}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      pickupLocation: e.target.value,
                    }))
                  }
                  placeholder={"Enter Pickup Location"}
                  label="Pickup Location"
                  size="sm"
                  required={false}
                  className="tiny-bordered-textfield"
                />
                {formErrors.pickupLocation && (
                  <small className="text-danger mt-2">
                    {formErrors.pickupLocation}
                  </small>
                )}
              </Col>
              <Col md={12}>
                <div className="d-flex justify-content-end mt-4">
                  <Button
                    size="xs"
                    label="Cancel"
                    variant="secondary"
                    disabled={isSubmitting}
                    onClick={onClose}
                  />
                  <Button
                    size="xs"
                    label="Map Terminal"
                    style={{ marginLeft: "1rem", width: "20%" }}
                    loading={isSubmitting}
                    disabled={!state.terminalId}
                    onClick={handleSubmit}
                    type="submit"
                    variant="primary"
                  />
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>
    </React.Fragment>
  );
};

export default MapTerminalModal;
